import { useState, useEffect } from 'react';
import {
  FiUsers,
  FiAlertTriangle,
  FiXCircle,
  FiBookOpen,
  FiFileText,
  FiUserCheck,
  FiClock,
  FiCheckCircle,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import StatCard from '../../components/StatCard';

const COLORS = ['#6B8063', '#D4A017', '#C0392B', '#3498DB', '#9B59B6'];
const BAR_COLORS = ['#6B8063', '#D4A017', '#3498DB', '#9B59B6', '#E67E22', '#C0392B', '#1ABC9C'];

/* ════════════════════════════════════════════
   Shared table styles
   ════════════════════════════════════════════ */

const tableHeaderStyle = {
  padding: '10px 14px',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#6b6b6b',
  textAlign: 'left',
  borderBottom: '2px solid #f0ece4',
};

const tableCellStyle = {
  padding: '10px 14px',
  fontSize: '0.82rem',
  color: '#2B362E',
};

const tableRowStyle = (i) => ({
  backgroundColor: i % 2 === 0 ? '#ffffff' : '#faf8f4',
  borderBottom: '1px solid #f0ece4',
});

/* ════════════════════════════════════════════
   Main Dashboard Component
   ════════════════════════════════════════════ */

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('lifeSupport');

  // Life Support + CPD shared data
  const [dash, setDash] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [deptBreakdown, setDeptBreakdown] = useState(null);

  // Post Graduate data
  const [traineeSummary, setTraineeSummary] = useState(null);
  const [evalStats, setEvalStats] = useState(null);

  // OJT data
  const [ojtStats, setOjtStats] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [d, c, s, t, db, ts, es, ojt] = await Promise.all([
          api.get('/reports/dashboard'),
          api.get('/reports/course-completion'),
          api.get('/reports/satisfaction'),
          api.get('/reports/attendance-timeline'),
          api.get('/reports/department-breakdown'),
          api.get('/trainees/stats/summary'),
          api.get('/evaluations/stats/summary'),
          api.get('/ojt/stats'),
        ]);
        setDash(d);
        setCompletion(c);
        setSatisfaction(s);
        setTimeline(t);
        setDeptBreakdown(db);
        setTraineeSummary(ts);
        setEvalStats(es);
        setOjtStats(ojt);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="card text-center p-4">
        <div className="spinner" style={{ margin: '40px auto' }} />
      </div>
    );
  }

  const tabButtonStyle = (tab) => ({
    padding: '8px 20px',
    fontSize: '0.85rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: activeTab === tab ? '#6B8063' : '#f0ece4',
    color: activeTab === tab ? '#ffffff' : '#2B362E',
    transition: 'all 0.2s ease',
  });

  return (
    <div>
      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <button style={tabButtonStyle('lifeSupport')} onClick={() => setActiveTab('lifeSupport')}>
          Life Support
        </button>
        <button style={tabButtonStyle('cpd')} onClick={() => setActiveTab('cpd')}>
          CPD Activities
        </button>
        <button style={tabButtonStyle('postGrad')} onClick={() => setActiveTab('postGrad')}>
          Training
        </button>
        <button style={tabButtonStyle('ojt')} onClick={() => setActiveTab('ojt')}>
          On Job Training
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'lifeSupport' && (
        <LifeSupportTab
          dash={dash}
          completion={completion}
          satisfaction={satisfaction}
          timeline={timeline}
          deptBreakdown={deptBreakdown}
        />
      )}

      {activeTab === 'cpd' && (
        <CPDTab
          dash={dash}
          satisfaction={satisfaction}
          timeline={timeline}
          deptBreakdown={deptBreakdown}
        />
      )}

      {activeTab === 'postGrad' && (
        <PostGraduateTab summary={traineeSummary} evalStats={evalStats} />
      )}

      {activeTab === 'ojt' && (
        <OJTTab stats={ojtStats} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   Tab 1: Life Support Dashboard
   ════════════════════════════════════════════ */

function LifeSupportTab({ dash, completion, satisfaction, timeline, deptBreakdown }) {
  // Course completion chart data
  const certStatusData = (completion?.summary || []).map((s) => ({
    name: s.course_name,
    'Total Courses': s.total_courses,
    'Completed': s.completed,
    'Total Attendees': s.total_attendees,
  }));

  // Course satisfaction
  const satisfactionData = (satisfaction?.course_satisfaction || []).map((s) => ({
    name: s.course_name,
    'Overall': ((Number(s.avg_q1) + Number(s.avg_q2) + Number(s.avg_q3) + Number(s.avg_q4)) / 4).toFixed(1),
    'Recommend %': Number(s.recommend_pct),
  }));

  // Attendance timeline (courses only)
  const courseTimeline = timeline?.course_attendance || [];
  const timelineData = courseTimeline.map((t) => ({
    month: t.month,
    'Attendance': t.attendance_count,
  }));

  // Department breakdowns
  const deptData = (deptBreakdown?.staff_by_department || []).slice(0, 10);
  const attendDeptData = (deptBreakdown?.attendance_by_department || []).slice(0, 10);

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <StatCard title="Total Staff" value={dash?.total_staff || 0} icon={FiUsers} />
        <StatCard title="Near Expiry Certs" value={dash?.staff_with_near_expiry_certs || 0} icon={FiAlertTriangle} color="#D4A017" />
        <StatCard title="Expired Certs" value={dash?.staff_with_expired_certs || 0} icon={FiXCircle} color="#C0392B" />
        <StatCard title="Courses Completed" value={dash?.completed_courses || 0} icon={FiBookOpen} color="#3498DB" />
      </div>

      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <StatCard title="Total Courses" value={dash?.total_courses || 0} icon={FiFileText} />
        <StatCard title="Total Course Attendance" value={dash?.total_course_attendance || 0} icon={FiUsers} color="#3498DB" />
      </div>

      {/* Row 1: Course Summary + Course Satisfaction */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Course Summary by Type</h4>
          {certStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={certStatusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Total Courses" fill="#3498DB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Completed" fill="#6B8063" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Total Attendees" fill="#D4A017" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No course data yet.</p>
          )}
        </div>

        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Course Satisfaction Scores</h4>
          {satisfactionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={satisfactionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Overall" fill="#6B8063" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No satisfaction data yet.</p>
          )}
        </div>
      </div>

      {/* Row 2: Attendance Timeline + Staff by Department */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Course Attendance Timeline</h4>
          {timelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Attendance" stroke="#6B8063" strokeWidth={2} dot={{ fill: '#6B8063' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No attendance data yet.</p>
          )}
        </div>

        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Staff by Department</h4>
          {deptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={deptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="staff_count" fill="#6B8063" radius={[0, 4, 4, 0]} name="Staff Count" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No department data yet.</p>
          )}
        </div>
      </div>

      {/* Row 3: Course Attendance by Department */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Course Attendance by Department</h4>
          {attendDeptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attendDeptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="total_attendance" fill="#D4A017" radius={[0, 4, 4, 0]} name="Attendance" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No attendance data yet.</p>
          )}
        </div>
        <div />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   Tab 2: CPD Activities (Grand Rounds)
   ════════════════════════════════════════════ */

function CPDTab({ dash, satisfaction, timeline, deptBreakdown }) {
  // Grand round satisfaction
  const grSatisfactionData = (satisfaction?.grand_round_satisfaction || []).map((s) => ({
    name: s.activity_type,
    'Overall': ((Number(s.avg_q1) + Number(s.avg_q2) + Number(s.avg_q3) + Number(s.avg_q4)) / 4).toFixed(1),
    'Recommend %': Number(s.recommend_pct),
  }));

  // Grand round attendance timeline
  const grTimeline = timeline?.grand_round_attendance || [];
  const grTimelineData = grTimeline.map((t) => ({
    month: t.month,
    'Attendance': t.attendance_count,
  }));

  // Attendance by department
  const attendDeptData = (deptBreakdown?.attendance_by_department || []).slice(0, 10);

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <StatCard title="Total Grand Rounds" value={dash?.total_grand_rounds || 0} icon={FiFileText} />
        <StatCard title="GR Attendance" value={dash?.total_grand_round_attendance || 0} icon={FiUsers} color="#3498DB" />
        <StatCard title="Total Courses" value={dash?.total_courses || 0} icon={FiBookOpen} />
        <StatCard title="Course Attendance" value={dash?.total_course_attendance || 0} icon={FiUsers} color="#D4A017" />
      </div>

      {/* Row 1: GR Satisfaction + GR Attendance Timeline */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Grand Round Satisfaction Scores</h4>
          {grSatisfactionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={grSatisfactionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Overall" fill="#3498DB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No grand round satisfaction data yet.</p>
          )}
        </div>

        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Grand Round Attendance Timeline</h4>
          {grTimelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={grTimelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Attendance" stroke="#3498DB" strokeWidth={2} dot={{ fill: '#3498DB' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No attendance data yet.</p>
          )}
        </div>
      </div>

      {/* Row 2: GR Attendance by Department */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Attendance by Department</h4>
          {attendDeptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attendDeptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="total_attendance" fill="#D4A017" radius={[0, 4, 4, 0]} name="Attendance" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No department attendance data yet.</p>
          )}
        </div>
        <div />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   Tab 3: Post Graduate Training
   ════════════════════════════════════════════ */

function PostGraduateTab({ summary, evalStats }) {
  const [selectedEval, setSelectedEval] = useState(null);
  const [evalDetail, setEvalDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const status = summary?.by_status || {};

  const byTypeData = (summary?.by_type || []).map((item) => ({
    name: item.training_type,
    count: item.count,
  }));

  const byDeptData = (summary?.by_department || []).map((item) => ({
    name: item.department,
    count: item.count,
  }));

  const FIELD_LABELS = {
    pre_rotation_communication: 'Pre-rotation Communication',
    orientation_organized: 'Orientation Organization',
    learning_opportunities: 'Learning Opportunities',
    supervisor_support: 'Supervisor Support',
    staff_professional: 'Staff Professionalism',
    department_organized: 'Department Organization',
    ata_coordination: 'ATA Coordination',
    overall_experience: 'Overall Experience',
  };

  const RATING_COLORS = { Excellent: '#6B8063', Good: '#3498DB', Fair: '#D4A017', Poor: '#C0392B' };

  const viewEvalDetail = async (traineeId, traineeName) => {
    setSelectedEval(traineeName);
    setLoadingDetail(true);
    try {
      const data = await api.get(`/evaluations/trainee/${traineeId}/detail`);
      setEvalDetail(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Prepare field averages chart data
  const fieldAvgData = evalStats?.fieldAverages
    ? Object.entries(evalStats.fieldAverages)
        .filter(([, v]) => v !== null)
        .map(([key, val]) => ({ name: FIELD_LABELS[key] || key, score: Number(val) }))
    : [];

  // Overall rating distribution for pie chart
  const ratingDistData = evalStats?.overallRatingCounts
    ? Object.entries(evalStats.overallRatingCounts)
        .filter(([, v]) => v > 0)
        .map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <StatCard title="Total Trainees" value={summary?.total || 0} icon={FiUsers} />
        <StatCard title="Active Trainees" value={status.active || 0} icon={FiUserCheck} color="#6B8063" />
        <StatCard title="Completed Training" value={status.completed || 0} icon={FiCheckCircle} color="#3498DB" />
        <StatCard title="Upcoming Trainees" value={status.upcoming || 0} icon={FiClock} color="#D4A017" />
      </div>

      {/* Evaluation Score Overview */}
      {evalStats && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiFileText size={18} color="#9B59B6" /> Rotation Evaluation Scores
          </h4>

          {evalStats.totalResponses === 0 ? (
            <p className="text-muted text-center" style={{ padding: '30px 0' }}>No evaluation responses received yet.</p>
          ) : (
            <>
              {/* Score summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <div style={{ padding: '16px', backgroundColor: '#f0f7ee', borderRadius: '10px', textAlign: 'center', border: '1px solid #d5e8d0' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#6B8063' }}>{evalStats.overallAverage}/4</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b6b6b', marginTop: '4px' }}>Overall Score</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#faf8f4', borderRadius: '10px', textAlign: 'center', border: '1px solid #f0ece4' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#2B362E' }}>{evalStats.totalResponses}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b6b6b', marginTop: '4px' }}>Total Responses</div>
                </div>
                <div style={{ padding: '16px', backgroundColor: '#eef4fb', borderRadius: '10px', textAlign: 'center', border: '1px solid #d0e0f0' }}>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#3498DB' }}>
                    {evalStats.totalResponses > 0 ? Math.round((evalStats.recommendCounts.Yes / evalStats.totalResponses) * 100) : 0}%
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b6b6b', marginTop: '4px' }}>Would Recommend</div>
                </div>
              </div>

              {/* Field-by-field scores chart + Rating distribution */}
              <div className="grid grid-2" style={{ marginBottom: '20px' }}>
                <div>
                  <h5 style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#2B362E' }}>Score by Category</h5>
                  {fieldAvgData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={Math.max(200, fieldAvgData.length * 36)}>
                      <BarChart data={fieldAvgData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                        <XAxis type="number" domain={[0, 4]} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
                        <Tooltip formatter={(v) => `${v}/4`} />
                        <Bar dataKey="score" name="Avg Score" fill="#9B59B6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted text-center">No data</p>
                  )}
                </div>
                <div>
                  <h5 style={{ marginBottom: '12px', fontSize: '0.85rem', color: '#2B362E' }}>Overall Rating Distribution</h5>
                  {ratingDistData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={ratingDistData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {ratingDistData.map((entry) => (
                            <Cell key={entry.name} fill={RATING_COLORS[entry.name] || '#B2AB9C'} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted text-center" style={{ padding: '40px 0' }}>No ratings yet</p>
                  )}
                </div>
              </div>

              {/* Per-trainee evaluation table */}
              <h5 style={{ marginBottom: '10px', fontSize: '0.85rem', color: '#2B362E' }}>Individual Evaluation Responses</h5>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Trainee</th>
                      <th style={tableHeaderStyle}>Type</th>
                      <th style={tableHeaderStyle}>Department</th>
                      <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Avg Score</th>
                      <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Rating</th>
                      <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Recommend</th>
                      <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evalStats.traineeEvaluations.map((ev, i) => (
                      <tr key={ev.id} style={tableRowStyle(i)}>
                        <td style={{ ...tableCellStyle, fontWeight: 600 }}>{ev.trainee_name}</td>
                        <td style={tableCellStyle}>{ev.training_type}</td>
                        <td style={tableCellStyle}>{ev.department}</td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            backgroundColor: Number(ev.avg_score) >= 3 ? '#e8f5e8' : Number(ev.avg_score) >= 2 ? '#fef9e7' : '#fdeded',
                            color: Number(ev.avg_score) >= 3 ? '#6B8063' : Number(ev.avg_score) >= 2 ? '#D4A017' : '#C0392B',
                            fontWeight: 600,
                            fontSize: '0.78rem',
                          }}>{ev.avg_score}/4</span>
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            backgroundColor: RATING_COLORS[ev.overall_rating] ? `${RATING_COLORS[ev.overall_rating]}18` : '#f5f0e8',
                            color: RATING_COLORS[ev.overall_rating] || '#6b6b6b',
                            fontWeight: 600,
                            fontSize: '0.78rem',
                          }}>{ev.overall_rating}</span>
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <span style={{
                            color: ev.recommend === 'Yes' ? '#6B8063' : ev.recommend === 'No' ? '#C0392B' : '#D4A017',
                            fontWeight: 600,
                          }}>{ev.recommend}</span>
                        </td>
                        <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                          <button
                            onClick={() => viewEvalDetail(ev.trainee_id, ev.trainee_name)}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#9B59B6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.72rem',
                              cursor: 'pointer',
                              fontWeight: 500,
                            }}
                          >View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Eval Detail Modal */}
      {selectedEval && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setSelectedEval(null); setEvalDetail(null); }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto', padding: '28px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#2B362E' }}>Evaluation: {selectedEval}</h3>
              <button onClick={() => { setSelectedEval(null); setEvalDetail(null); }} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b6b6b' }}>&#x2715;</button>
            </div>

            {loadingDetail ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : evalDetail?.evaluation ? (
              <div>
                {/* Trainee info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                  {[
                    ['Type', evalDetail.trainee.training_type],
                    ['Department', evalDetail.trainee.department],
                    ['Period', evalDetail.trainee.training_period || `${evalDetail.trainee.start_date} - ${evalDetail.trainee.end_date}`],
                    ['Supervisor', evalDetail.trainee.supervisor_name || 'N/A'],
                  ].map(([label, val]) => (
                    <div key={label} style={{ padding: '8px 12px', backgroundColor: '#faf8f4', borderRadius: '6px' }}>
                      <div style={{ fontSize: '0.62rem', color: '#B2AB9C', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: '0.82rem', color: '#2B362E', fontWeight: 500 }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Rating items */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '16px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...tableHeaderStyle, fontSize: '0.72rem' }}>Item</th>
                      <th style={{ ...tableHeaderStyle, textAlign: 'center', fontSize: '0.72rem' }}>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(FIELD_LABELS).map(([key, label], i) => {
                      const val = evalDetail.evaluation[key];
                      return (
                        <tr key={key} style={tableRowStyle(i)}>
                          <td style={tableCellStyle}>{label}</td>
                          <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 12px',
                              borderRadius: '12px',
                              backgroundColor: RATING_COLORS[val] ? `${RATING_COLORS[val]}18` : '#f5f0e8',
                              color: RATING_COLORS[val] || '#6b6b6b',
                              fontWeight: 600,
                              fontSize: '0.78rem',
                            }}>{val || 'N/A'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Text responses */}
                {evalDetail.evaluation.best_part && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#2B362E', marginBottom: '4px' }}>Best part of the rotation:</div>
                    <div style={{ padding: '10px 14px', backgroundColor: '#faf8f4', borderRadius: '8px', fontSize: '0.82rem', color: '#3a3a3a', lineHeight: 1.6 }}>{evalDetail.evaluation.best_part}</div>
                  </div>
                )}
                {evalDetail.evaluation.suggestions && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#2B362E', marginBottom: '4px' }}>Suggestions for improvement:</div>
                    <div style={{ padding: '10px 14px', backgroundColor: '#faf8f4', borderRadius: '8px', fontSize: '0.82rem', color: '#3a3a3a', lineHeight: 1.6 }}>{evalDetail.evaluation.suggestions}</div>
                  </div>
                )}

                {/* Bottom summary */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <div style={{ flex: 1, padding: '10px', backgroundColor: '#f0f7ee', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: '#6b6b6b', textTransform: 'uppercase' }}>Overall Rating</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: RATING_COLORS[evalDetail.evaluation.overall_rating] || '#2B362E' }}>{evalDetail.evaluation.overall_rating}</div>
                  </div>
                  <div style={{ flex: 1, padding: '10px', backgroundColor: '#eef4fb', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: '#6b6b6b', textTransform: 'uppercase' }}>Recommend</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: evalDetail.evaluation.recommend === 'Yes' ? '#6B8063' : evalDetail.evaluation.recommend === 'No' ? '#C0392B' : '#D4A017' }}>{evalDetail.evaluation.recommend}</div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted text-center" style={{ padding: '30px 0' }}>No evaluation submitted for this trainee.</p>
            )}
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Trainees by Type</h4>
          {byTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(200, byTypeData.length * 44)}>
              <BarChart data={byTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip />
                <Bar dataKey="count" name="Trainees" radius={[0, 4, 4, 0]}>
                  {byTypeData.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>No training type data available.</p>
          )}
        </div>

        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Trainees by Department</h4>
          {byDeptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(200, byDeptData.length * 44)}>
              <BarChart data={byDeptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip />
                <Bar dataKey="count" name="Trainees" fill="#6B8063" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>No department data available.</p>
          )}
        </div>
      </div>

      {/* Detail Tables Row */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Detailed Breakdown by Type</h4>
          {byTypeData.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Training Type</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {byTypeData.map((row, i) => (
                  <tr key={i} style={tableRowStyle(i)}>
                    <td style={tableCellStyle}>{row.name}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 600 }}>{row.count}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f0ece4' }}>
                  <td style={{ ...tableCellStyle, fontWeight: 700 }}>Total</td>
                  <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>
                    {byTypeData.reduce((sum, r) => sum + r.count, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>No data available.</p>
          )}
        </div>

        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Detailed Breakdown by Department</h4>
          {byDeptData.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Department</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {byDeptData.map((row, i) => (
                  <tr key={i} style={tableRowStyle(i)}>
                    <td style={tableCellStyle}>{row.name}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 600 }}>{row.count}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f0ece4' }}>
                  <td style={{ ...tableCellStyle, fontWeight: 700 }}>Total</td>
                  <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>
                    {byDeptData.reduce((sum, r) => sum + r.count, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>No data available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   Tab 4: On Job Training
   ════════════════════════════════════════════ */

function OJTTab({ stats }) {
  if (!stats) return <p className="text-muted text-center p-4">No OJT data available.</p>;

  const deptData = (stats.byDept || []).map((d) => ({
    name: d.department,
    Records: d.count,
  }));

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard title="Total OJT Records" value={stats.total || 0} icon={FiBookOpen} color="#3498DB" />
        <StatCard title="OUL Approved" value={stats.oulApproved || 0} icon={FiCheckCircle} color="#6B8063" />
        <StatCard title="Pending Approval" value={(stats.total || 0) - (stats.oulApproved || 0)} icon={FiClock} color="#D4A017" />
        <StatCard title="Total Cost (SAR)" value={stats.totalCost ? Number(stats.totalCost).toLocaleString() : '0'} icon={FiFileText} color="#9B59B6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        {/* By Department */}
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>OJT by Department</h4>
          {deptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={deptData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="Records" fill="#3498DB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No department data yet.</p>
          )}
        </div>

        {/* OUL Approval Breakdown */}
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>OUL Approval Status</h4>
          {stats.total > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Approved', value: stats.oulApproved || 0 },
                    { name: 'Pending', value: (stats.total || 0) - (stats.oulApproved || 0) },
                  ]}
                  cx="50%" cy="50%" outerRadius={80}
                  dataKey="value" label={({ name, value }) => `${name}: ${value}`}
                >
                  <Cell fill="#6B8063" />
                  <Cell fill="#D4A017" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center p-4">No data yet.</p>
          )}
        </div>
      </div>

      {/* Recent Records Table */}
      <div className="card">
        <h4 style={{ marginBottom: '16px' }}>Recent OJT Records</h4>
        {(stats.recentRecords || []).length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr>
                {['Employee', 'Department', 'Course', 'Date', 'Cost (SAR)', 'OUL'].map((h) => (
                  <th key={h} style={tableHeaderStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.recentRecords.map((r, i) => (
                <tr key={i} style={tableRowStyle(i)}>
                  <td style={tableCellStyle}>{r.employee_name}</td>
                  <td style={tableCellStyle}>{r.department}</td>
                  <td style={tableCellStyle}>{r.course_name}</td>
                  <td style={tableCellStyle}>{r.date}</td>
                  <td style={tableCellStyle}>{r.course_cost ? Number(r.course_cost).toLocaleString() : '—'}</td>
                  <td style={tableCellStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 600,
                      backgroundColor: r.oul_approval ? '#e8f5e9' : '#fff8e1',
                      color: r.oul_approval ? '#6B8063' : '#D4A017',
                    }}>
                      {r.oul_approval ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted text-center p-4">No OJT records yet.</p>
        )}
      </div>
    </div>
  );
}
